// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file CommandPaletteItem.tsx
 * @input Uses React, StyleX, CommandPaletteContext, DialogContext
 * @output Exports CommandPaletteItem component
 * @position Sub-component; individual selectable item
 *
 * SYNC: When modified, update:
 * - /packages/cli/templates/blocks/components/CommandPalette/ (showcase blocks)
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { mergeProps, mergeRefs } from "../utils/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { useCommandPaletteContext } from "./CommandPaletteContext.js";
import { useDialogContext } from "../Dialog/DialogContext.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx } from "react/jsx-runtime";
const HOVER_HOVER = '@media (hover: hover)';
const styles = {
  item: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    kzqmXN: "xh8yej3",
    kg3NbH: "xrrkdod",
    k8WAf4: "xce4md1",
    kaIpWk: "xx3sua9",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib",
    kMwMTN: "x1tgivj0",
    kWkggS: "xjbqb8w",
    kkrTdU: "x1ypdohk",
    k9WMMc: "xdpxx8g",
    kI3sdo: "x1a2a7pz",
    kfSwDN: "x87ps6o",
    $$css: true
  },
  itemHover: {
    kHE3J0: "xe9uy6x",
    kSReZ0: "xyxi2l3",
    $$css: true
  },
  itemHighlighted: {
    kWkggS: "x1lmrjuc",
    $$css: true
  },
  itemDisabled: {
    kSiTet: "xbyyjgo",
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  itemSelected: {
    kWkggS: "xgcxg3y",
    $$css: true
  }
};
/**
 * A selectable item in the command palette.
 * Accepts arbitrary children for full rendering control.
 *
 * When used inside CommandPalette, registers with context for
 * keyboard navigation and selection. Can also be used
 * standalone with explicit isHighlighted/isSelected props.
 *
 * @compositionHint Place inside CommandPaletteList or CommandPaletteGroup.
 *
 * @example
 * ```
 * <CommandPaletteItem value="settings" onSelect={() => navigate('/settings')}>
 *   Settings
 * </CommandPaletteItem>
 * ```
 */
export function CommandPaletteItem({
  value,
  onSelect,
  isHighlighted: controlledHighlighted,
  isSelected: controlledSelected,
  isDisabled = false,
  children,
  ref,
  xstyle,
  className,
  style,
  ...props
}) {
  const ctx = useCommandPaletteContext();
  const dialogContext = useDialogContext();
  const isInlineDialog = dialogContext?.isInline === true;
  const itemRef = useRef(null);
  const didMountRef = useRef(false);

  // Find this item's index in the flat selectable items list (DOM order).
  // This aligns with useCombobox's index-based navigation.
  const itemIndex = useMemo(() => ctx?.selectableItems.findIndex(item => item.value === value) ?? -1, [ctx?.selectableItems, value]);

  // Highlight from useCombobox: index-based, matches DOM order
  const isHighlighted = controlledHighlighted ?? (ctx ? ctx.highlightedIndex === itemIndex && itemIndex >= 0 : false);
  const isSelected = controlledSelected ?? (ctx ? ctx.value === value : false);
  useEffect(() => {
    // Inline dialogs are documentation/showcase previews. Avoid scrolling the
    // surrounding page when picker mode auto-highlights its selected item on
    // mount, while preserving scroll-into-view after user navigation.
    const shouldSkipInitialInlineScroll = isInlineDialog && !didMountRef.current;
    didMountRef.current = true;
    if (shouldSkipInitialInlineScroll) {
      return;
    }
    if (isHighlighted && itemRef.current) {
      itemRef.current.scrollIntoView?.({
        block: 'nearest'
      });
    }
  }, [isHighlighted, isInlineDialog]);
  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }
    onSelect?.(value);
    if (ctx) {
      ctx.selectItem(value);
      ctx.onClose();
    }
  }, [isDisabled, value, onSelect, ctx]);
  const handleMouseEnter = useCallback(() => {
    if (isDisabled || !ctx || itemIndex < 0) {
      return;
    }
    ctx.setHighlightedIndex(itemIndex);
  }, [isDisabled, itemIndex, ctx]);
  return /*#__PURE__*/_jsx("div", {
    ref: mergeRefs(ref, itemRef),
    id: ctx && itemIndex >= 0 ? ctx.getItemId(itemIndex) : undefined,
    role: "option",
    "aria-selected": isSelected,
    "aria-disabled": isDisabled || undefined,
    "data-value": value,
    onClick: handleClick,
    onMouseEnter: handleMouseEnter,
    ...mergeProps(themeProps('command-palette-item'), stylex.props(styles.item, !isDisabled && styles.itemHover, isHighlighted && styles.itemHighlighted, isSelected && styles.itemSelected, isDisabled && styles.itemDisabled, xstyle), className, style),
    ...props,
    children: children
  });
}
CommandPaletteItem.displayName = 'CommandPaletteItem';